import { PageSizePoints } from './scale-calibration';

/** Interaction offered by the Preview Area tool. */
export type AreaShape = 'select' | 'rectangle' | 'ellipse' | 'polygon' | 'freehand';

/** A point stored as fractions of the displayed PDF page. */
export interface NormalizedPoint {
  x: number;
  y: number;
}

export interface RectangleAreaGeometry {
  kind: 'rectangle';
  start: NormalizedPoint;
  end: NormalizedPoint;
}

export interface EllipseAreaGeometry {
  kind: 'ellipse';
  start: NormalizedPoint;
  end: NormalizedPoint;
}

export interface PolygonAreaGeometry {
  kind: 'polygon';
  vertices: readonly NormalizedPoint[];
}

/** Normalized geometry contract reused by single-shape and cumulative area measurement. */
export type AreaGeometry = RectangleAreaGeometry | EllipseAreaGeometry | PolygonAreaGeometry;

export interface AreaShapeOption {
  shape: AreaShape;
  label: string;
  icon: string;
}

export const AREA_SHAPE_OPTIONS: readonly AreaShapeOption[] = [
  { shape: 'select', label: 'Select & move', icon: 'open_with' },
  { shape: 'rectangle', label: 'Rectangle', icon: 'crop_free' },
  { shape: 'ellipse', label: 'Oval', icon: 'circle' },
  { shape: 'polygon', label: 'Polygon', icon: 'pentagon' },
  { shape: 'freehand', label: 'Freehand', icon: 'gesture' },
];

/** Metadata used to label and render an Area shape. */
export function areaShapeOption(shape: AreaShape): AreaShapeOption {
  return AREA_SHAPE_OPTIONS.find(option => option.shape === shape) ?? AREA_SHAPE_OPTIONS[0];
}

/** Sides of the polygon that stands in for an oval when an area is sent to chat. */
const OVAL_OUTLINE_SIDES = 64;

/**
 * The closed outline of an area as a polygon in the same normalized coordinates (Issue #2542).
 *
 * The chat attachment workflow of Issue #1747 crops one polygon. A rectangle becomes its four
 * corners, a polygon is returned as drawn, and an oval becomes a 64-sided polygon that follows it.
 */
export function areaOutline(geometry: AreaGeometry): NormalizedPoint[] {
  if (geometry.kind === 'polygon') return geometry.vertices.map(point => ({ ...point }));
  const left = Math.min(geometry.start.x, geometry.end.x);
  const right = Math.max(geometry.start.x, geometry.end.x);
  const top = Math.min(geometry.start.y, geometry.end.y);
  const bottom = Math.max(geometry.start.y, geometry.end.y);
  if (geometry.kind === 'rectangle') {
    return [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];
  }
  const centerX = (left + right) / 2;
  const centerY = (top + bottom) / 2;
  const radiusX = (right - left) / 2;
  const radiusY = (bottom - top) / 2;
  return Array.from({ length: OVAL_OUTLINE_SIDES }, (_, index) => {
    const angle = (2 * Math.PI * index) / OVAL_OUTLINE_SIDES;
    return { x: centerX + radiusX * Math.cos(angle), y: centerY + radiusY * Math.sin(angle) };
  });
}

/** Area in displayed-page PDF points squared. */
export function areaInPagePoints(geometry: AreaGeometry, pageSize: PageSizePoints): number {
  if (geometry.kind === 'ellipse') {
    const radiusX = (Math.abs(geometry.end.x - geometry.start.x) * pageSize.width) / 2;
    const radiusY = (Math.abs(geometry.end.y - geometry.start.y) * pageSize.height) / 2;
    return Math.PI * radiusX * radiusY;
  }
  if (geometry.kind === 'polygon') {
    return polygonAreaInPagePoints(geometry.vertices, pageSize);
  }
  return (
    Math.abs(geometry.end.x - geometry.start.x) *
    pageSize.width *
    Math.abs(geometry.end.y - geometry.start.y) *
    pageSize.height
  );
}

/**
 * Length of an area's outline in displayed-page PDF points (Issue #2542).
 *
 * A rectangle and a polygon are exact. An oval has no closed-form perimeter; this uses Ramanujan's
 * second approximation, which is within a few millionths for any oval drawn on a plan and exact
 * for a circle.
 */
export function perimeterInPagePoints(geometry: AreaGeometry, pageSize: PageSizePoints): number {
  if (geometry.kind === 'polygon') return ringLengthInPagePoints(geometry.vertices, pageSize);
  const width = Math.abs(geometry.end.x - geometry.start.x) * pageSize.width;
  const height = Math.abs(geometry.end.y - geometry.start.y) * pageSize.height;
  if (geometry.kind === 'rectangle') return 2 * (width + height);
  const a = width / 2;
  const b = height / 2;
  if (a + b === 0) return 0;
  const h = ((a - b) * (a - b)) / ((a + b) * (a + b));
  return Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
}

/** Length of a closed ring of normalized points, including the edge back to its first point. */
export function ringLengthInPagePoints(ring: readonly NormalizedPoint[], pageSize: PageSizePoints): number {
  if (ring.length < 2) return 0;
  let total = 0;
  for (let index = 0; index < ring.length; index++) {
    total += distanceInPagePoints(ring[index], ring[(index + 1) % ring.length], pageSize);
  }
  return total;
}

/** Distance between normalized points in displayed-page PDF points. */
export function distanceInPagePoints(start: NormalizedPoint, end: NormalizedPoint, pageSize: PageSizePoints): number {
  return Math.hypot((end.x - start.x) * pageSize.width, (end.y - start.y) * pageSize.height);
}

/** Shoelace area in displayed-page PDF points squared. */
export function polygonAreaInPagePoints(vertices: readonly NormalizedPoint[], pageSize: PageSizePoints): number {
  if (vertices.length < 3) return 0;
  return (Math.abs(signedDoubleArea(vertices)) * pageSize.width * pageSize.height) / 2;
}

export interface PolygonValidation {
  valid: boolean;
  message: string | null;
}

/** Validate a simple, closed polygon before displaying an enclosed area. */
export function validatePolygon(vertices: readonly NormalizedPoint[]): PolygonValidation {
  const distinct = deduplicatePoints(vertices);
  if (distinct.length < 3) {
    return { valid: false, message: 'Add at least three distinct vertices.' };
  }
  if (hasSelfIntersection(distinct)) {
    return { valid: false, message: 'Edges cannot cross. Remove or reposition a vertex.' };
  }
  if (Math.abs(signedDoubleArea(distinct)) <= 1e-12) {
    return { valid: false, message: 'The vertices must enclose an area.' };
  }
  return { valid: true, message: null };
}

/** Remove zero-length consecutive edges and an explicit closing vertex without changing topology. */
function deduplicatePoints(vertices: readonly NormalizedPoint[]): NormalizedPoint[] {
  const result: NormalizedPoint[] = [];
  for (const vertex of vertices) {
    if (result.length === 0 || !pointsEqual(result[result.length - 1], vertex)) result.push(vertex);
  }
  if (result.length > 1 && pointsEqual(result[0], result[result.length - 1])) result.pop();
  return result;
}

/** Signed shoelace sum, twice the normalized polygon area. */
function signedDoubleArea(vertices: readonly NormalizedPoint[]): number {
  let result = 0;
  for (let index = 0; index < vertices.length; index++) {
    const current = vertices[index];
    const next = vertices[(index + 1) % vertices.length];
    result += current.x * next.y - next.x * current.y;
  }
  return result;
}

/** Whether any two non-adjacent boundary segments intersect. */
function hasSelfIntersection(vertices: readonly NormalizedPoint[]): boolean {
  for (let first = 0; first < vertices.length; first++) {
    const firstNext = (first + 1) % vertices.length;
    for (let second = first + 1; second < vertices.length; second++) {
      const secondNext = (second + 1) % vertices.length;
      if (first === second || firstNext === second || secondNext === first) continue;
      if (first === 0 && secondNext === 0) continue;
      if (segmentsIntersect(vertices[first], vertices[firstNext], vertices[second], vertices[secondNext])) return true;
    }
  }
  return false;
}

/** Inclusive line-segment intersection test. */
function segmentsIntersect(a: NormalizedPoint, b: NormalizedPoint, c: NormalizedPoint, d: NormalizedPoint): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  if (oppositeSigns(abC, abD) && oppositeSigns(cdA, cdB)) return true;
  return (
    (nearZero(abC) && onSegment(a, b, c)) ||
    (nearZero(abD) && onSegment(a, b, d)) ||
    (nearZero(cdA) && onSegment(c, d, a)) ||
    (nearZero(cdB) && onSegment(c, d, b))
  );
}

/** Signed turn of three points. */
function orientation(a: NormalizedPoint, b: NormalizedPoint, c: NormalizedPoint): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** Whether two orientation values are meaningfully on opposite sides of zero. */
function oppositeSigns(first: number, second: number): boolean {
  return (first > 1e-12 && second < -1e-12) || (first < -1e-12 && second > 1e-12);
}

/** Floating-point zero comparison used by geometry predicates. */
function nearZero(value: number): boolean {
  return Math.abs(value) <= 1e-12;
}

/** Whether a collinear point lies inside a closed segment. */
function onSegment(start: NormalizedPoint, end: NormalizedPoint, point: NormalizedPoint): boolean {
  const epsilon = 1e-12;
  return (
    point.x >= Math.min(start.x, end.x) - epsilon &&
    point.x <= Math.max(start.x, end.x) + epsilon &&
    point.y >= Math.min(start.y, end.y) - epsilon &&
    point.y <= Math.max(start.y, end.y) + epsilon
  );
}

/** Coordinate equality within the normalized geometry tolerance. */
function pointsEqual(first: NormalizedPoint, second: NormalizedPoint): boolean {
  return Math.abs(first.x - second.x) <= 1e-12 && Math.abs(first.y - second.y) <= 1e-12;
}
